import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import kebabCase from 'lodash/kebabCase'

import { Flex } from 'reflexbox/styled-components'
import styled, { css, createGlobalStyle } from 'styled-components'
import AntSelect from 'antd/lib/select'
import 'antd/lib/select/style/index.less'

import theme from 'theme'
import media from 'media'

import UniversalLink from 'components/UniversalLink'
import ArrowLink from 'components/ArrowLink'

import { getHeightTo } from 'lib/dom'

import {
  TAG_ALL_ARTICLES,
  NAV_BAR_HEIGHT,
  MOBILE_NAV_BAR_HEIGHT,
  CLASS_NO_LINK_STYLES,
} from 'consts'

const { Option } = AntSelect

const selectedTagStyles = css`
  cursor: pointer;
  font-weight: ${theme.fontWeights.bold} !important;
  position: relative;

  &:after {
    content: '';
    display: block;
    background: ${theme.colors.green};
    height: 3px;
    width: 100%;
    position: absolute;
    bottom: -4px;
  }
`

export const Container = styled(Flex).attrs({
  className: 'filter-container',
  alignItems: 'center',
})`
  height: 70px;
  background: white;
  z-index: 10;
  position: sticky;
  top: ${MOBILE_NAV_BAR_HEIGHT - 1}px;

  ${media.desktop`
    top: ${NAV_BAR_HEIGHT - 1}px;
  `}
`

const TagsContainer = styled(Flex).attrs({
  justifyContent: 'space-between',
  alignItems: 'center',
  mt: [10, 10, 10, 0],
})`
  width: 100%;
`

const Tag = styled(UniversalLink)
  .attrs({
    className: CLASS_NO_LINK_STYLES,
  })
  .withConfig({
    shouldForwardProp: (prop) => ['focus'].indexOf(prop) === -1,
  })`
  text-decoration: none !important;
  font-weight: ${theme.fontWeights.thin} !important;
  margin-left: 5px;
  margin-right: 5px;
  color: ${theme.colors.black};

  ${({ focus }) => focus && selectedTagStyles}

  &:hover {
    color: ${theme.colors.black} !important;
    text-decoration: none !important;
    ${selectedTagStyles}
  }

  display: none;

  ${media.desktop`
    display: block;
  `}
`

const StyledArrowLink = styled(ArrowLink)`
  && {
    margin-top: 0;
    /* color: ${theme.colors.black} !important; */
  }
  span {
    margin-right: 5px !important;
  }
`

const BlogArrowLink = styled(StyledArrowLink)`
  display: none !important;

  ${media.desktop`
    display: flex !important;
  `}

  ${media.giant`
    display: none !important;
  `}
`

const HelpCenterLink = styled(StyledArrowLink)`
  ${media.desktop`
    display: none !important;
  `}

  ${media.giant`
    display: flex !important;
  `}
`

// HACK: Dirty dirty - from app, need to consolidate themes into one
const GlobalStyle = createGlobalStyle`
  .ant-select-selector {
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    padding-top: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0;
    margin-right: 0;
    font-size: 16px;
    height: 36px;
    line-height: 34px;
    box-shadow: none !important;

    height: 36px !important;

    .ant-select-selection-search {
      left: 0px;
      right: 0px;
      .ant-select-selection-search-input {
        height: 34px;
      }
    }
    .ant-select-selection-item {
      margin-left: 0;
      margin-right: 0;
      line-height: 34px;
    }
  }

  .ant-select-dropdown .ant-select-item-option-selected {
    background-color: #fafafa;
  }
  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: ${theme.colors.blues[10]}
  }
`

const Dropdown = styled(AntSelect)``

const DropdownContainer = styled(Flex)
  .attrs({
    flexDirection: 'column',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['fullWidth'].indexOf(prop) === -1,
  })`
  width: ${({ fullWidth }) => (fullWidth ? 100 : 50)}%;

  ${media.desktop`
    display: none;
  `}
`

const StyledOption = styled(Option)``

const OptionLink = styled(UniversalLink)`
  display: inline-block;
  width: 100%;
  color: inherit;
  text-decoration: none !important;
`

const FilterTitle = styled.div`
  font-size: ${theme.fontSizes[30]}px;
  font-weight: ${theme.fontWeights.lightest};

  margin-bottom: 6px;
`

const getTagLink = (tag, baseSlug, scrollName) => {
  return tag === TAG_ALL_ARTICLES
    ? `/${baseSlug}/#${scrollName}`
    : `/${baseSlug}/${kebabCase(tag)}/#${scrollName}`
}

const Filter = ({
  values,
  onClicksArray,
  selectedValue,
  baseSlug,
  referenceLink,
  scrollName,
  heightThreshold,
  dropdownProps,
  filterTitle,
  ...others
}) => {
  useEffect(() => {
    const handleNonDebouncedScroll = () => {
      if (heightThreshold === undefined || heightThreshold === null) {
        return
      }

      const mobileNav = document.querySelector('.filter-container')

      if (
        window.scrollY >=
        (heightThreshold ||
          getHeightTo({ selector: '#summary-filter' }) - NAV_BAR_HEIGHT - 1)
      ) {
        mobileNav.style.borderBottom = `2px solid ${theme.colors.blacks[10]}`
        mobileNav.style.zIndex = '10'
      } else {
        mobileNav.style.borderBottom = 'none'
        mobileNav.style.zIndex = '0'
      }
    }

    handleNonDebouncedScroll()
    document.addEventListener('scroll', handleNonDebouncedScroll)

    return () => {
      document.removeEventListener('scroll', handleNonDebouncedScroll)
    }
  }, [heightThreshold])

  const getTagContent = (t) => (t === TAG_ALL_ARTICLES ? 'All' : t)

  const useOnClick = !!onClicksArray

  // note: antd is weird and the option onClick prop doesn't work.
  // giving it to a random div inside works
  const getOption = (t, onClick) => {
    return useOnClick ? (
      <StyledOption key={t} value={t}>
        <div onClick={onClick}>{getTagContent(t)}</div>
      </StyledOption>
    ) : (
      <StyledOption key={t} value={t}>
        <OptionLink
          onClick={(e) => {
            if (t === selectedValue) {
              e.preventDefault()
            }
          }}
          to={getTagLink(t, baseSlug, scrollName)}
        >
          {getTagContent(t)}
        </OptionLink>
      </StyledOption>
    )
  }

  // TODO: prob should make id a prop from react
  return (
    <Container id="summary-filter" data-cy="post-filter" {...others}>
      <TagsContainer>
        <DropdownContainer fullWidth={!referenceLink}>
          <GlobalStyle />
          {filterTitle && <FilterTitle>{filterTitle}</FilterTitle>}
          <Dropdown
            defaultValue={selectedValue}
            value={selectedValue}
            {...dropdownProps}
          >
            {values.map((t, i) => getOption(t, onClicksArray?.[i]))}
          </Dropdown>
        </DropdownContainer>
        {values.map((t) => (
          <Tag
            focus={selectedValue.toLowerCase() === t.toLowerCase()}
            key={t}
            to={getTagLink(t, baseSlug, scrollName)}
          >
            {getTagContent(t)}
          </Tag>
        ))}
        {referenceLink && (
          <>
            <HelpCenterLink width={40} to={referenceLink.to}>
              {referenceLink.mobileText}
            </HelpCenterLink>
            <BlogArrowLink width={40} to={referenceLink.to}>
              {referenceLink.desktopText}
            </BlogArrowLink>
          </>
        )}
      </TagsContainer>
    </Container>
  )
}

export default Filter

Filter.propTypes = {
  // TODO: change this to array that takes { key, value }
  values: PropTypes.array.isRequired,
  // must be sorted the same way as values
  onClicksArray: PropTypes.array,
  selectedValue: PropTypes.string.isRequired,
  baseSlug: PropTypes.string,
  referenceLink: PropTypes.shape({
    to: PropTypes.string.isRequired,
    mobileText: PropTypes.string.isRequired,
    desktopText: PropTypes.string.isRequired,
  }),
  scrollName: PropTypes.string,
  navBarHeight: PropTypes.number,
  heightThreshold: PropTypes.number,
  // anything from AntSelect
  dropdownProps: PropTypes.shape({ autoFocus: PropTypes.bool }),
}
